import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { FormInputText } from '../../inputs/FormInputText';
import { CustomMUIButton } from '../../commons/buttons/CustomMUIButton';
import { TextButton } from '../../commons/buttons/TextButton';
import { ApiError } from '../../../models/ApiError';
import { ButtonClassNames, LocalStorageItems, LoginRouteNames } from '../../../models/Common';
import { authorizationApi } from '../../../services/apiServices/user/authorization';

interface ForgotPasswordProps {
  handleBack: () => void;
  handleLink: (link: LoginRouteNames) => void;
}

export const ForgotPassword: FC<ForgotPasswordProps> = ({ handleBack, handleLink }) => {
  const { t } = useTranslation();
  const methods = useForm<{ phoneEmail: string }>();
  const { handleSubmit, setError } = methods;
  const [passwordReset, { isLoading }] = authorizationApi.usePasswordResetMutation();

  const submitForgotPassword = async ({ phoneEmail }: { phoneEmail: string }) => {
    const isPhone = phoneEmail.length === 10 && !phoneEmail.includes('@');
    try {
      await passwordReset({ authId: phoneEmail })
        .unwrap()
        .then(() => {
          if (isPhone) {
            localStorage.setItem(LocalStorageItems.Phone, phoneEmail);
            handleLink(LoginRouteNames.PasswordRecoveryByPhoneForm);
          } else {
            localStorage.setItem(LocalStorageItems.Email, phoneEmail);
            handleLink(LoginRouteNames.PasswordRecoveryByEmailForm);
          }
        });
    } catch (err) {
      const error = err as ApiError;
      setError('phoneEmail', { type: 'server', message: error?.data?.error?.message });
    }
  };

  const handleForgotDetails = () => {
    handleLink(LoginRouteNames.PasswordRecoveryAssistanceForm);
  };

  return (
    <div className="forgot-password-container">
      <p className="forgot-password-container__title login-page__title">{t('auth.forgotPassword')}</p>
      <FormProvider {...methods}>
        <form className="forgot-password-container__form" onSubmit={handleSubmit(submitForgotPassword)}>
          <FormInputText
            name="phoneEmail"
            className="primary"
            label={t('auth.phoneEmail')}
            isRequired
            helperText={t('auth.enterEmailPhoneInFormat')}
          />
          <div className="forgot-password-container__form__btn-group">
            <CustomMUIButton
              label={t('common.back')}
              handleClick={handleBack}
              className={ButtonClassNames.SecondaryOutlined}
              isBold
            />
            <CustomMUIButton
              label={t('buttons.continue')}
              className={ButtonClassNames.PrimaryFulfilled}
              isBold
              type="submit"
              isLoading={isLoading}
            />
          </div>
          <div className="forgot-password-container__form__btn-text">
            <TextButton
              label={t('auth.iForgotMyDetails')}
              handleClick={handleForgotDetails}
              className="primary"
              isArrow={false}
            />
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
